<!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
<template>
  <div
    :class="['x-modal__overlay', size]"
    :style="{ marginLeft: `${centerOffset}px` }"
    @click.self="$emit('overlay-clicked')"
  >
    <div
      :class="['x-modal__box', 'appearance__' + appearance, size]"
      data-test="modal"
    >
      <!-- @slot The text/content to display inside the modal -->
      <slot></slot>
    </div>
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
/**
 * Modal dialog component for displaying content in an elevated frame above the main body content
 *
 * ```jsx
 * import xModal from '@/components/shared/x-modal'
 *
 * <x-modal>Modal Content Here</x-modal>
 * ```
 *
 * @deprecated Use `<ys-alert-dialog>` from the latest `@onxmaps/yellowstone-ui` release instead.
 */
export default {
  name: 'XModal',
  inject: {
    theme: { default: 'hunt' },
  },
  props: {
    size: {
      type: String,
      default: 'small',
      validator: (value) =>
        [
          'small',
          'medium',
          'large',
          'extra-large',
          'auto',
          'full-width',
        ].indexOf(value) !== -1,
    },
    centerOffset: {
      type: Number,
      default: 0,
    },
  },
  emits: ['overlay-clicked'],
  computed: {
    appearance() {
      if (this.theme === 'hunt') {
        return 'orange'
      } else if (this.theme === 'backcountry') {
        return 'blue'
      } else if (this.theme === 'offroad') {
        return 'grey'
      } else if (this.theme === 'fish') {
        return 'green'
      }
      return 'orange'
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_type.scss';
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/system/_variables.scss';

.x-modal__overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(33, 33, 33, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: variables.$z-index-level-4;
}

.x-modal__box {
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 140px);
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  position: relative;
  background: #ffffff;
  border-radius: 10px;
  text-align: center;

  &.small {
    width: 305px;
  }

  &.medium {
    width: 355px;
  }

  &.large {
    width: 435px;
  }

  &.extra-large {
    width: 483px;
  }

  &.full-width {
    width: 100%;
    height: 100%;

    @include breakpoints.mobile {
      max-width: 100%;
    }
  }

  &.auto {
    width: auto;
  }

  &.appearance__orange {
    :slotted(button) {
      color: colors.$BrandHunt;
    }

    :slotted(button.x-modal__actions-inverse) {
      color: colors.$Grey100;
    }
  }

  &.appearance__grey {
    :slotted(button) {
      color: colors.$Grey100;
    }

    :slotted(button.x-modal__actions-inverse) {
      color: colors.$Grey70;
    }
  }

  &.appearance__blue {
    :slotted(button) {
      color: colors.$BrandBackcountry;
    }

    :slotted(button.x-modal__actions-inverse) {
      color: colors.$Grey100;
    }
  }

  &.appearance__green {
    :slotted(button) {
      color: colors.$BrandFish;
    }

    :slotted(button.x-modal__actions-inverse) {
      color: colors.$Grey100;
    }
  }

  :slotted(.x-modal__contents) {
    padding: 26px;
  }

  :slotted(h3) {
    font-size: 21px;
    line-height: 28px;
    font-weight: 100;
    padding: 10px 10px 0;
  }

  :slotted(p) {
    font-size: 14px;
    line-height: 22px;
    color: colors.$Grey80;
    padding: 0 16px;
  }

  :slotted(.x-modal__actions) {
    display: flex;
    font-weight: 900;
    border-top: 1px solid colors.$Grey30;

    button {
      width: 100%;
      background: none;
      border-radius: 0;
      font-size: 16px;
      line-height: 24px;
      padding: 1em;
    }

    button.x-modal__actions-inverse {
      border-right: 1px solid colors.$Grey30;
    }

    button.x-modal__actions-destructive {
      color: var(--ys-color-alert-error);
    }
  }

  :slotted(.x-modal__actions-vertical) {
    padding: var(--ys-spacing-250);
    display: flex;
    flex-direction: column;
    gap: var(--ys-spacing-100);
    border-top: 1px solid colors.$Grey30;

    div {
      text-align: center;
    }
  }

  :slotted(.x-modal__actions-vertical-primary > button) {
    display: inline-block;
    color: colors.$BaseWhite;
    width: 100%;
  }

  &.appearance__grey :slotted(.x-modal__actions-vertical-primary > button) {
    color: colors.$BaseBlack;
  }

  :slotted(.x-modal__actions-vertical-primary .x-button) {
    display: flex;
    margin: 0 auto 10px;
  }

  :slotted(.x-modal__actions-vertical-secondary) {
    > button,
    > a {
      @include type.button1Type;
      display: inline-block;
      margin-bottom: 1rem;
      padding: 0;
      background: transparent;
      color: colors.$Grey70;
      cursor: pointer;
    }
  }

  @include breakpoints.mobile {
    max-width: calc(100vw - 40px);
    margin: 20px;
  }
}
</style>
